import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Alert from "react-bootstrap/Alert";
import InputGroup from "react-bootstrap/InputGroup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faEye, 
  faEyeSlash, 
  faLock, 
  faShieldHalved,
  faCircleExclamation
} from "@fortawesome/free-solid-svg-icons";
import { portalResetPassword, resetPassword } from "../services/authService";
import { useToast } from "../contexts/ToastContext";
import { useLoading } from "../contexts/LoadingContext";

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { startLoading, stopLoading } = useLoading();
  
  const token = searchParams.get("token");
  const isPortal = window.location.pathname.includes("/portal/reset-password");
  
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleFieldChange = (field, value) => {
    if (field === "newPassword") {
      setNewPassword(value);
      setValidationErrors((prev) => ({ ...prev, newPassword: undefined, confirmPassword: undefined }));
    }
    if (field === "confirmPassword") {
      setConfirmPassword(value);
      setValidationErrors((prev) => ({ ...prev, confirmPassword: undefined }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!newPassword) {
      errors.newPassword = "New password is required";
    } else if (newPassword.length < 6) {
      errors.newPassword = "Password must be at least 6 characters";
    }
    
    if (!confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
    } else if (newPassword && newPassword !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccess(false);

    if (!token) {
      setErrorMsg("Reset token is missing from the URL. Please request a new link.");
      return;
    }

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      startLoading();
      if (isPortal) {
        await portalResetPassword(token, newPassword);
      } else {
        await resetPassword(token, newPassword);
      }
      setSuccess(true);
      showToast("Password reset successfully!", "success");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setErrorMsg(err.message || "Failed to reset password. The link may have expired.");
      showToast(err.message || "Failed to reset password.", "error");
    } finally {
      stopLoading();
    }
  };

  const loginUrl = isPortal ? "/portal/login" : "/login";

  return (
    <section>
      <style dangerouslySetInnerHTML={{__html: `
        .rp-input-group {
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          overflow: hidden;
          transition: all 0.2s ease-in-out;
          background-color: #fff;
        }
        .rp-input-group:focus-within {
          border-color: #275317;
          box-shadow: 0 0 0 3px rgba(39, 83, 23, 0.15);
        }
        .rp-input-group.border-danger:focus-within {
          border-color: #dc3545;
          box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.15);
        }
        .rp-input-group .input-group-text {
          background-color: #f8fafc;
          border: none;
          color: #64748b;
          width: 44px;
          justify-content: center;
        }
        .rp-input-group .form-control {
          border: none;
          padding: 0.7rem 0.8rem;
          font-size: 0.95rem;
        }
        .rp-input-group .form-control:focus {
          box-shadow: none;
        }
        .rp-input-group .btn-toggle {
          background-color: transparent;
          border: none;
          color: #64748b;
          padding: 0 0.85rem;
        }
        .rp-input-group .btn-toggle:hover {
          color: #275317;
        }
      `}} />

      <Container
        className="d-flex align-items-center justify-content-center login-background"
        fluid
      >
        <div className="login-modal">
          <div className="d-flex justify-content-center">
            <img src="/logo.png" alt="Logo" className="login-logo" />
          </div>
          
          <h5 className="text-center mb-1" style={{ color: "#275317", fontWeight: "600" }}>
            Reset Password
          </h5>
          <p className="text-center text-muted mb-4" style={{ fontSize: "14px" }}>
            Enter your new password below.
          </p>

          {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}
          
          {success ? (
            <div className="text-center">
              <Alert variant="success" className="mb-4">
                Your password has been reset successfully.
              </Alert>
              <Link to={loginUrl}>
                <Button className="login-button animate-fade-in" size="xl">
                  Back to Login
                </Button>
              </Link>
            </div>
          ) : (
            <Form onSubmit={handleSubmit}>
              {/* New Password Field */}
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold text-dark">New Password</Form.Label>
                <InputGroup className={`rp-input-group ${validationErrors.newPassword ? "border-danger" : ""}`}>
                  <InputGroup.Text>
                    <FontAwesomeIcon icon={faLock} />
                  </InputGroup.Text>
                  <Form.Control
                    type={showNew ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => handleFieldChange("newPassword", e.target.value)}
                    placeholder="Enter new password (min. 6 chars)"
                    isInvalid={!!validationErrors.newPassword}
                  />
                  <Button 
                    type="button" 
                    variant="link" 
                    className="btn-toggle text-decoration-none d-flex align-items-center"
                    onClick={() => setShowNew(!showNew)}
                  >
                    <FontAwesomeIcon icon={showNew ? faEyeSlash : faEye} />
                  </Button>
                </InputGroup>
                {validationErrors.newPassword && (
                  <Form.Text className="text-danger d-block mt-1">
                    <FontAwesomeIcon icon={faCircleExclamation} className="me-1" />
                    {validationErrors.newPassword}
                  </Form.Text>
                )}
              </Form.Group>

              {/* Confirm Password Field */}
              <Form.Group className="mb-4">
                <Form.Label className="fw-semibold text-dark">Confirm Password</Form.Label>
                <InputGroup className={`rp-input-group ${validationErrors.confirmPassword ? "border-danger" : ""}`}>
                  <InputGroup.Text>
                    <FontAwesomeIcon icon={faShieldHalved} />
                  </InputGroup.Text>
                  <Form.Control
                    type={showConfirm ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => handleFieldChange("confirmPassword", e.target.value)}
                    placeholder="Confirm your new password"
                    isInvalid={!!validationErrors.confirmPassword}
                  />
                  <Button 
                    type="button" 
                    variant="link" 
                    className="btn-toggle text-decoration-none d-flex align-items-center"
                    onClick={() => setShowConfirm(!showConfirm)}
                  >
                    <FontAwesomeIcon icon={showConfirm ? faEyeSlash : faEye} />
                  </Button>
                </InputGroup>
                {validationErrors.confirmPassword && (
                  <Form.Text className="text-danger d-block mt-1">
                    <FontAwesomeIcon icon={faCircleExclamation} className="me-1" />
                    {validationErrors.confirmPassword}
                  </Form.Text>
                )}
              </Form.Group>

              <Button
                className="login-button mt-2"
                size="xl"
                type="submit"
              >
                Reset Password
              </Button>
              
              <div className="text-center mt-3">
                <Link to={loginUrl} style={{ color: "#275317", textDecoration: "none", fontSize: "14px" }}>
                  ← Back to Login
                </Link>
              </div>
            </Form>
          )}
        </div>
      </Container>
    </section>
  );
}

export default ResetPassword;
